import { atom } from "nanostores"
import { Play, PlayFolder, Syncable } from '../types';
import { client } from '../apiClient';
import { $isOnline } from './app';

export const $playFolderModalIsOpen = atom<{ isOpen: boolean, folder?: Syncable<PlayFolder>}>({ isOpen: false, folder: undefined });
export const $playFolderSelectModalIsOpen = atom<boolean>(false);
export const $playFolderAssignModalIsOpen = atom<{ isOpen: boolean, play: Play | null}>({ isOpen: false, play: null });
export const $playFolders = atom<Syncable<PlayFolder>[]>([]);
export const $playFoldersIsLoading = atom<boolean>(true);

$playFolders.subscribe(folders => {
  console.log('playFolders::changed', folders)
})

async function fetchPlayFoldersFromCache() {
  let allFolders: Syncable<PlayFolder>[] = [];
  console.log('fetchPlayFoldersFromCache')
  try {
    const playFoldersCache = await caches.open('playFolders')
    if (playFoldersCache) {
      console.log('fetchPlayFoldersFromCache::cache', playFoldersCache)
      console.log('keys', await playFoldersCache.keys())
      const response = await playFoldersCache.match('folders')
      if (response) {
        const data = await response.json()
        console.log('cache:data', data)
        allFolders = data as Syncable<PlayFolder>[]
      } else {
        console.log('noresponse', 'folders')
      }
    }
  } catch (e) {
    console.error('fetchPlayFoldersFromCache::error', e)
  }
  console.log('fetchPlayFoldersFromCache:result', allFolders)
  return allFolders
}

export async function fetchPlayFolders(): Promise<Syncable<PlayFolder>[]> {
  let allFolders: Syncable<PlayFolder>[] = [];
  let nextToken: string | undefined | null = undefined;
  let hasError: any[] | undefined = undefined;

  if (!$isOnline.get()) {
    return await fetchPlayFoldersFromCache()
  }

  do {
    const { data: folders, errors, nextToken: nT } = await client.models.PlayFolder.list({ nextToken, limit: 150 })
    if (!folders && errors && errors.length > 0) {
      hasError = errors
    }
    if (folders && folders.length > 0) {
      allFolders = allFolders.concat(folders as Syncable<PlayFolder>[])
    }
    nextToken = nT

  } while (nextToken && !hasError)

  if (hasError) {
    return await fetchPlayFoldersFromCache()
  }
  const playFoldersCache = caches.open('playFolders')
  if (playFoldersCache) {
    playFoldersCache.then(async cache => {
      const blob = new Blob([JSON.stringify(allFolders)], { type: 'application/json' })
      const response = new Response(blob)
      await cache.put('folders', response)
      console.log('folders saved to cache')
    }).catch(() => {
      console.error("Failed to save folders to cache")
    })
  }

  return allFolders
}

export async function savePlayFolder(folder: Syncable<PlayFolder>, fromSync?: boolean): Promise<Syncable<PlayFolder>> {

  if (!$isOnline.get()) {
    if (fromSync) {
      return folder
    }
    await addFolderToSyncQueue(folder)
    const _folders = $playFolders.get()
    const index = _folders.findIndex(f => f.id === folder.id)
    if (index !== -1) {
      const updatedFolders = [..._folders]
      updatedFolders[index] = folder
      $playFolders.set(updatedFolders)
    } else {
      $playFolders.set([..._folders, folder]);
    }
    return folder
  }
  const { isDraft, synced, ...payload } = folder

  // This is a new folder
  if (folder.isDraft) {
    const { data, errors } = await client.models.PlayFolder.create(payload)
    if (data && data.id === folder.id) {
      folder.synced = true
      folder.isDraft = false

      try {
        const currentDraftCache = await caches.open('draftPlayFolders')
        await currentDraftCache.delete(`folder-${folder.id}`)
      } catch (e) {
        console.error("Failed to remove folder from sync queue", { cause: e })
      }

    }
    // If there were errors, add folder to sync queue
    if (errors && errors.length > 0 && !fromSync) {
      await addFolderToSyncQueue(folder)
    }

    // Add folder to local store
    const _folders = $playFolders.get()
    const index = _folders.findIndex(f => f.id === folder.id)
    if (index !== -1) {
      const updatedFolders = [..._folders]
      updatedFolders[index] = folder
      $playFolders.set(updatedFolders)
    } else {
      $playFolders.set([..._folders, folder]);
    }
  } else {
    const { data, errors } = await client.models.PlayFolder.update({
      ...payload,
      id: folder.id!
    })
    if (data && data.id === folder.id) {
      folder.synced = true
      // Remove folder from sync queue if it exists
      try {
        const currentDraftCache = await caches.open('draftPlayFolders')
        await currentDraftCache.delete(`folder-${folder.id}`)
      } catch (e) {
        console.error("Failed to remove folder from sync queue", { cause: e })
      }
    }
    if (errors && errors.length > 0 && !fromSync) {
      await addFolderToSyncQueue(folder)
    }
    // Update folder in local store
    const _folders = $playFolders.get()
    const index = _folders.findIndex(f => f.id === folder.id)
    if (index !== -1) {
      const updatedFolders = [..._folders]
      updatedFolders[index] = folder
      $playFolders.set(updatedFolders)
    }
  }

  return folder
}

const addFolderToSyncQueue = async (folder: Syncable<PlayFolder>) => {
  const currentDraftCache = caches.open('draftPlayFolders')
  if (currentDraftCache) {
    currentDraftCache.then(cache => {
      const blob = new Blob([JSON.stringify(folder)], { type: 'application/json' })
      const response = new Response(blob)
      cache.put(`folder-${folder.id}`, response)
    }).catch(() => {
      throw new Error("Failed to save folder to sync queue", { cause: 'Failed to open cache'})
    })
  }
}

const getUnsyncedFolders = async () => {
    let folders: Syncable<PlayFolder>[] = []

    const currentDraftCache = await caches.open('draftPlayFolders')
    try {
      const keys = await currentDraftCache.keys()
      const promises = keys.map(async key => {
        const response = await currentDraftCache.match(key);
          if (response) {
              return response.json();
          }
      })
      const data = await Promise.all(promises)
      folders = data.filter(f => f !== null) as Syncable<PlayFolder>[]
    } catch (e) {
      console.error("Failed to get keys from cache", { cause: e })
    }
    return folders
}

export async function getFolders() {
  $playFoldersIsLoading.set(true)
  console.log('getFolders')
  const folders = await fetchPlayFolders()
  console.log('folders', folders)
  const unsyncedFolders = await getUnsyncedFolders()
  console.log('unsyncedFolders', unsyncedFolders)
  // Remove any folders from the fetched list that are in the unsynced list
  const filteredFolders = folders.filter(f => !unsyncedFolders.some(uf => uf.id === f.id))
  $playFolders.set([...filteredFolders, ...unsyncedFolders])
  $playFoldersIsLoading.set(false)
  return [...folders, ...unsyncedFolders]
}

export async function syncPlayFolders () {
  console.log('syncPlayFolders')
  try {
    const draftCache = await getUnsyncedFolders()
    console.log('draftCache', draftCache)
    if (draftCache.length > 0) {
      const folders = await Promise.all(draftCache.map(async folder => {
        return savePlayFolder(folder, true)
      }))
      console.log('folders', folders)
    }
  } catch (e) {}
}